/**
 * industrySignals.js — iter-288
 * ================================
 * Maps an industry id to the "welcome hero" signal metadata that best
 * resonates for operators in that industry. Each entry returns an array
 * of 2-3 ``{ icon, label, valueKey }`` objects the hero reads off the
 * live dashboard metrics payload.
 *
 * Keeps the WelcomeHero itself industry-agnostic — the signal translation
 * happens at the integration site (UnifiedDashboard) so the hero stays
 * reusable.
 */
import {
  CheckCircle2, Clock, Zap, Utensils, ShoppingBag, Calendar, Package,
  Users, Stethoscope, Hammer, GraduationCap, Home as HomeIcon, Plane,
  DollarSign, Sparkles, Target,
} from 'lucide-react';


// Default (tenant-generic) signals — used when no industry match is found.
const DEFAULT_SIGNALS = [
  { icon: CheckCircle2, label: 'Done', key: 'completedCount', format: 'count' },
  { icon: Clock, label: 'Pending', key: 'pendingCount', format: 'count' },
  { icon: Zap, label: 'High priority', key: 'highPriorityCount', format: 'count' },
];


// Per-industry mapping. Shape: each entry returns an array of signal
// descriptors. ``key`` is looked up against the ``metrics`` prop (with
// fallback to the default counts). ``format`` controls display.
export const INDUSTRY_SIGNALS = {
  hospitality: [
    { icon: Utensils, label: 'Covers today', key: 'coversToday', fallback: 'pendingCount' },
    { icon: Calendar, label: 'Bookings', key: 'bookingsToday', fallback: 'completedCount' },
    { icon: Target, label: 'KPI', key: 'kpiScore', format: 'score' },
  ],
  retail: [
    { icon: ShoppingBag, label: 'Orders today', key: 'ordersToday', fallback: 'completedCount' },
    { icon: Package, label: 'Fulfil pending', key: 'fulfilPending', fallback: 'pendingCount' },
    { icon: Target, label: 'KPI', key: 'kpiScore', format: 'score' },
  ],
  ecommerce: [
    { icon: ShoppingBag, label: 'Orders 24h', key: 'ordersToday', fallback: 'completedCount' },
    { icon: DollarSign, label: 'Revenue', key: 'revenueToday', format: 'currency' },
    { icon: Target, label: 'KPI', key: 'kpiScore', format: 'score' },
  ],
  health: [
    { icon: Stethoscope, label: 'Appointments', key: 'appointmentsToday', fallback: 'pendingCount' },
    { icon: Users, label: 'Waitlist', key: 'waitlistCount', fallback: 'highPriorityCount' },
    { icon: Target, label: 'KPI', key: 'kpiScore', format: 'score' },
  ],
  health_wellness: [
    { icon: Stethoscope, label: 'Appointments', key: 'appointmentsToday', fallback: 'pendingCount' },
    { icon: Users, label: 'Waitlist', key: 'waitlistCount', fallback: 'highPriorityCount' },
    { icon: Target, label: 'KPI', key: 'kpiScore', format: 'score' },
  ],
  education: [
    { icon: GraduationCap, label: 'Classes', key: 'classesToday', fallback: 'pendingCount' },
    { icon: Users, label: 'Enrolled', key: 'studentsEnrolled', fallback: 'completedCount' },
    { icon: Target, label: 'KPI', key: 'kpiScore', format: 'score' },
  ],
  construction: [
    { icon: Hammer, label: 'Jobs active', key: 'jobsActive', fallback: 'pendingCount' },
    { icon: Users, label: 'Crew deployed', key: 'crewDeployed', fallback: 'completedCount' },
    { icon: Target, label: 'KPI', key: 'kpiScore', format: 'score' },
  ],
  trades: [
    { icon: Hammer, label: 'Jobs active', key: 'jobsActive', fallback: 'pendingCount' },
    { icon: Users, label: 'Crew deployed', key: 'crewDeployed', fallback: 'completedCount' },
    { icon: Target, label: 'KPI', key: 'kpiScore', format: 'score' },
  ],
  real_estate: [
    { icon: HomeIcon, label: 'Listings', key: 'listingsActive', fallback: 'completedCount' },
    { icon: Calendar, label: 'Inspections', key: 'inspectionsToday', fallback: 'pendingCount' },
    { icon: Target, label: 'KPI', key: 'kpiScore', format: 'score' },
  ],
  travel: [
    { icon: Plane, label: 'Bookings', key: 'bookingsToday', fallback: 'completedCount' },
    { icon: Users, label: 'Guests in-house', key: 'guestsInHouse', fallback: 'pendingCount' },
    { icon: Target, label: 'KPI', key: 'kpiScore', format: 'score' },
  ],
};


const _fmt = (value, format) => {
  if (value === null || value === undefined || value === '') return '—';
  if (format === 'currency') {
    const n = Number(value) || 0;
    if (n >= 1_000_000) return `$${(n / 1_000_000).toFixed(1)}M`;
    if (n >= 1_000) return `$${(n / 1_000).toFixed(1)}k`;
    return `$${n.toFixed(0)}`;
  }
  if (format === 'score') {
    return `${value}/100`;
  }
  return value;
};


/**
 * Build the signals array for an industry.
 *
 * @param {string} industryId - The tenant's industry key.
 * @param {object} metrics - Flat metrics object (``{ coversToday, kpiScore, ... }``).
 * @param {object} defaults - Fallback counts ``{ completedCount, pendingCount, highPriorityCount }``.
 */
export function buildIndustrySignals(industryId, metrics = {}, defaults = {}) {
  const key = (industryId || '').toLowerCase();
  const map = INDUSTRY_SIGNALS[key] || null;
  const signals = map || DEFAULT_SIGNALS;
  return signals.map((s) => {
    const rawValue = metrics[s.key] ?? (s.fallback ? defaults[s.fallback] : undefined) ?? defaults[s.key];
    return {
      icon: s.icon,
      label: s.label,
      value: _fmt(rawValue, s.format),
    };
  });
}
